import { SupabaseClient } from '@supabase/supabase-js';
import { getGlobalLatticePrices, getBatchGlobalLatticePrices } from '../../services/supabaseService';
import { computePricePerUnit } from './intelligence';

export interface LatticeBenchmarkItem {
  price: number;
  canonicalId?: string | null;
  name?: string | null;
  size?: number | string | null;
  unit?: string | null;
  embedding?: number[] | null;
  plu_code?: string | null;
  upc?: string | null;
  gtin?: string | null;
}

export interface LatticeBetterDeal {
  price: number;
  storeName: string;
  address: string;
  distance: number | null;
  latitude: number | null;
  longitude: number | null;
  date: string;
  /** Distinct shoppers who reported this item at that store within the Horizon (null when the database doesn't say). */
  reports: number | null;
}

export interface LatticeBenchmark {
  // Best comparable price in the Geospatial Lattice ("the World Record"), or
  // null when there is nothing trustworthy to compare against.
  latticeRecord: number | null;
  // Distinct shoppers behind that record at its store (null when unknown).
  latticeReports: number | null;
  // Set only when that record beats the item's own price.
  betterDeal: LatticeBetterDeal | null;
  // Set when the item's price is way off (over 10x, or under a tenth of) what
  // the exact same product usually costs nearby: likely a misread price or
  // quantity. latticeRecord and betterDeal stay null so it adds no leakage.
  priceCheck: LatticePriceCheck | null;
}

export interface LatticePriceCheck {
  // Median nearby price of the exact product, same size and unit.
  usualPrice: number;
}

// An item priced more than this many times off the usual Lattice price, either
// way, is flagged as a likely misread instead of being compared.
const PRICE_CHECK_RATIO = 10;

/**
 * Reports a Lattice record needs before the app calls a price a Lattice-Beater:
 * one shopper's single receipt isn't enough to say a price beats the area.
 */
export const MIN_LATTICE_REPORTS = 2;

const reportsOf = (row: any): number | null => {
  const n = Number(row?.sample_count);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const sameText = (a: unknown, b: unknown) =>
  String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
const sameSize = (a: unknown, b: unknown) => {
  const x = parseFloat(String(a ?? ''));
  const y = parseFloat(String(b ?? ''));
  if (Number.isNaN(x) || Number.isNaN(y)) return Number.isNaN(x) && Number.isNaN(y);
  return Math.abs(x - y) < 0.001;
};

/**
 * Confirms a suspected way-off price against the exact product only: same
 * slug, same size and unit. Returns the usual nearby price when the item's
 * price is more than PRICE_CHECK_RATIO times off it, otherwise null, so fuzzy
 * (embedding/name) matches never flag anything.
 */
async function confirmPriceCheck(
  supabase: SupabaseClient,
  item: LatticeBenchmarkItem,
  slug: string,
  ctx: { lat?: number | null; lng?: number | null; radiusMiles: number; horizonHours: number },
  keep: (p: any) => boolean
): Promise<LatticePriceCheck | null> {
  if (!(item.price > 0)) return null;
  let rows: any[] = [];
  try {
    rows = await getBatchGlobalLatticePrices(supabase, [slug], ctx.lat ?? undefined, ctx.lng ?? undefined, ctx.radiusMiles, ctx.horizonHours);
  } catch {
    return null;
  }
  const exact = (rows || []).filter((p: any) =>
    p.item_slug === slug && keep(p) && sameText(p.unit, item.unit) && sameSize(p.size, item.size) && Number(p.price) > 0
  );
  if (exact.length === 0) return null;
  const usualPrice = median(exact.map((p: any) => Number(p.price)));
  const ratio = item.price / usualPrice;
  return ratio > PRICE_CHECK_RATIO || ratio < 1 / PRICE_CHECK_RATIO ? { usualPrice } : null;
}

/**
 * The Ultra "Ghost Pulse" lattice comparison for one item: fetches nearby
 * Lattice prices and picks the best comparable one. Shared by the basket's
 * GhostPulse (live, per item) and the Bulk Import background worker, so a
 * bulk-imported trip gets the same leakage/savings numbers as a basket trip.
 */
export async function findLatticeBenchmark(
  supabase: SupabaseClient,
  item: LatticeBenchmarkItem,
  ctx: { lat?: number | null; lng?: number | null; radiusMiles: number; horizonHours: number; now?: Date }
): Promise<LatticeBenchmark> {
  const none: LatticeBenchmark = { latticeRecord: null, latticeReports: null, betterDeal: null, priceCheck: null };
  const slug = item.canonicalId;
  if (!slug) return none;
  const latestPrice = item.price;
  const now = ctx.now || new Date();

  // For multi-count packs (e.g. "12ct", "3ct"), embedding similarity is too broad —
  // a 12ct entry scores >0.85 against 2ct or 6ct entries of the same product,
  // causing cross-quantity price comparisons. The slug already encodes the count
  // and is the correct identifier. PLU codes are also per-unit, not per-pack.
  const isMultiCountPack = Number(item.size) > 1 && item.unit?.toLowerCase() === 'ct';
  const prices = await getGlobalLatticePrices(
    supabase,
    slug,
    ctx.lat ?? undefined,
    ctx.lng ?? undefined,
    ctx.radiusMiles,
    ctx.horizonHours,
    isMultiCountPack ? null : item.embedding,
    item.name ?? undefined,
    isMultiCountPack ? null : item.plu_code,
    item.upc,
    item.gtin
  );
  if (!prices || prices.length === 0) return none;

  // LATTICE-BEATER PROTOCOL v2.4: Temporal-Geospatial Filtering
  // We only exclude the user's OWN contribution IF it's at the same location AND same day.
  const today = now.toDateString();
  const isValid = (p: any) => {
    const isMyContribution = p.is_own_pulse;
    const isSameLocation = p.distance_miles !== null && p.distance_miles < 0.1;
    const isSameDay = new Date(p.price_date).toDateString() === today;
    // Rule 1: Self-loop check (Same User + Same Spot + Same Day = Exclude)
    // We allow same-store deals if they are from the past, surfacing price hikes.
    if (isMyContribution && isSameLocation && isSameDay) return false;
    // Rule 2: Everything else is a valid deal (including historical prices at this store)
    return true;
  };
  const validPrices = prices.filter(isValid);
  if (validPrices.length === 0) return none;

  // Rank by price-per-unit against candidates sharing the item's exact unit
  // — same-unit only, no cross-unit conversion (oz<->lb) yet. A cheaper raw
  // price from a smaller pack is not actually a better deal. Falls back to
  // raw price when no candidate has comparable size/unit data, so items
  // without size/unit info keep working exactly as before.
  const latestUnit = item.unit?.trim().toLowerCase() || null;
  const latestPricePerUnit = computePricePerUnit(latestPrice, item.size as any);

  const withPricePerUnit = validPrices.map((p: any) => ({
    ...p,
    pricePerUnit: computePricePerUnit(Number(p.price), p.size),
  }));
  const comparable = (latestPricePerUnit != null && latestUnit)
    ? withPricePerUnit.filter((p: any) => p.pricePerUnit != null && p.unit?.trim().toLowerCase() === latestUnit)
    : [];

  const usingPerUnit = comparable.length > 0;
  const pool = usingPerUnit ? comparable : withPricePerUnit;

  const best = usingPerUnit
    ? pool.reduce((min: any, p: any) => p.pricePerUnit < min.pricePerUnit ? p : min, pool[0])
    : pool.reduce((min: any, p: any) => Number(p.price) < Number(min.price) ? p : min, pool[0]);
  const bestPriceNum = Number(best.price);

  // Sanity guard: a legitimate deal should be at least 10% of the current price.
  // Anything below that is almost certainly a cross-product embedding match
  // (e.g. a single egg at $0.88 matching a 12ct carton at $1.88). Kept as a
  // defense-in-depth backstop even with per-unit ranking now in place — it
  // catches cases where the size/unit data itself is missing or wrong.
  // When the guard trips, or the price is under a tenth of the record, check
  // the exact product before calling the price itself way off.
  if (latestPrice > 0 && (bestPriceNum < latestPrice * 0.10 || latestPrice < bestPriceNum * 0.10)) {
    const priceCheck = await confirmPriceCheck(supabase, item, slug, ctx, isValid);
    if (priceCheck) return { ...none, priceCheck };
    if (bestPriceNum < latestPrice * 0.10) return none;
  }

  // "Better" means a lower price-per-unit when comparable, otherwise falls
  // back to the raw-price comparison used before size/unit data existed.
  const isBetter = usingPerUnit
    ? (latestPricePerUnit === 0 || best.pricePerUnit < latestPricePerUnit! - 0.001)
    : (latestPrice === 0 || bestPriceNum < latestPrice - 0.01);

  return {
    latticeRecord: bestPriceNum,
    latticeReports: reportsOf(best),
    betterDeal: isBetter
      ? {
          price: bestPriceNum,
          storeName: best.store_name,
          address: best.store_address,
          distance: best.distance_miles,
          latitude: best.latitude,
          longitude: best.longitude,
          date: best.price_date,
          reports: reportsOf(best),
        }
      : null,
    priceCheck: null,
  };
}
